import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../api'
import { useAuthStore } from '../../store/authStore'
import AdminNotificationBell from './AdminNotificationBell'

interface MenuItem {
  label: string
  href?: string
  icon: string
  children?: { label: string; href: string }[]
}


const MENU_ITEMS: MenuItem[] = [
  { label: '대시보드', href: '/admin', icon: '📊' },
  { label: '메뉴관리', href: '/admin/menu', icon: '☰' },
  { label: '페이지관리', href: '/admin/page', icon: '📄' },
  {
    label: '게시판관리', icon: '📰',
    children: [
      { label: '공지사항관리', href: '/admin/notice' },
      { label: '행사관리', href: '/admin/event' },
      { label: '추가 게시판 관리', href: '/admin/board' },
    ]
  },
  { label: '출석관리', href: '/admin/attendance', icon: '✅' },
  { label: '포인트관리', href: '/admin/point', icon: '💰' },
  { label: '프로그램', href: '/admin/program', icon: '🎯' },
  { label: '상담마루관리', href: '/admin/counsel', icon: '💬' },
  { label: '마루공간관리', href: '/admin/space', icon: '🏠' },
  { label: '금고관리', href: '/admin/locker', icon: '🔐' },
  { label: '마루책방관리', href: '/admin/library', icon: '📚' },
  { label: '기관/휴무일 관리', href: '/admin/company', icon: '🏢' },
  {
    label: '키오스크관리', icon: '📱',
    children: [
      { label: '메뉴관리', href: '/admin/kiosk/menu' },
      { label: '접근성관리', href: '/admin/kiosk/accessibility' },
      { label: '화면디자인설정', href: '/admin/kiosk/display' },
    ]
  },
  {
    label: '회원관리', icon: '👥',
    children: [
      { label: '회원정보관리', href: '/admin/member' },
      { label: '희망도서관리', href: '/admin/wishbook' },
    ]
  },
]

interface AdminSidebarProps {
  open?: boolean
  onClose?: () => void
}

export default function AdminSidebar({ open = true, onClose }: AdminSidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { clearAuth } = useAuthStore()
  const [expandedMenu, setExpandedMenu] = useState<string | null>(() => {
    const matched = MENU_ITEMS.find(item =>
      item.children?.some(c => location.pathname.startsWith(c.href))
    )
    return matched?.label ?? null
  })

  const handleLogout = async () => {
    try {
      await fetchWithAuth(getApiUrl('/marubit/api/member/logout'), { method: 'POST' })
    } catch {}
    clearAuth()
    navigate('/')
  }

  const isActive = (item: MenuItem) => {
    if (item.href === '/admin') return location.pathname === '/admin'
    if (item.href) return location.pathname === item.href || location.pathname.startsWith(item.href + '/')
    if (item.children) return item.children.some(c => location.pathname.startsWith(c.href))
    return false
  }

  const sidebarContent = (
    <div className="flex flex-col h-full bg-[#2a1f1a] text-white" style={{ width: '260px' }}>
      {/* 로고 */}
      <div className="px-6 py-5 border-b border-white/10">
        <div className="flex items-center justify-between">
          <Link to="/admin" className="no-underline" onClick={onClose}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#887267] rounded-lg flex items-center justify-center text-white font-bold text-[14px]">M</div>
              <div>
                <p className="text-white font-bold text-[15px] m-0">마루빛</p>
                <p className="text-white/50 text-[11px] m-0">관리자</p>
              </div>
            </div>
          </Link>

          <AdminNotificationBell />



        </div>
      </div>

      {/* 메뉴 */}
      <nav className="flex-1 overflow-y-auto py-3">
        {MENU_ITEMS.map(item => {
          const active = isActive(item)
          const hasChildren = !!item.children?.length
          const isExpanded = expandedMenu === item.label

          return (
            <div key={item.label}>
              {hasChildren ? (
                <>
                  <button
                    onClick={() => setExpandedMenu(isExpanded ? null : item.label)}
                    className={[
                      'w-full flex items-center justify-between px-5 py-[10px] text-[14px] border-none cursor-pointer transition-colors',
                      active ? 'bg-[#887267]/30 text-white' : 'bg-transparent text-white/70 hover:text-white hover:bg-white/5',
                    ].join(' ')}
                  >
                    <span className="flex items-center gap-3">
                      <span className="text-[16px]">{item.icon}</span>
                      {item.label}
                    </span>
                    <span className={`text-[12px] transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                  </button>
                  {isExpanded && (
                    <div className="bg-black/20">
                      {item.children!.map(sub => (
                        <Link
                          key={sub.label}
                          to={sub.href}
                          onClick={onClose}
                          className={[
                            'block pl-[52px] pr-5 py-[8px] text-[13px] no-underline transition-colors',
                            location.pathname === sub.href
                              ? 'text-[#f8b62c] font-semibold'
                              : 'text-white/60 hover:text-white',
                          ].join(' ')}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.href!}
                  onClick={onClose}
                  className={[
                    'flex items-center gap-3 px-5 py-[10px] text-[14px] no-underline transition-colors',
                    active ? 'bg-[#887267] text-white font-semibold' : 'text-white/70 hover:text-white hover:bg-white/5',
                  ].join(' ')}
                >
                  <span className="text-[16px]">{item.icon}</span>
                  {item.label}
                </Link>
              )}
            </div>
          )
        })}
      </nav>

      {/* 하단 */}
      <div className="border-t border-white/10 py-3">
        <Link
          to="/"
          onClick={onClose}
          className="flex items-center gap-3 px-5 py-[10px] text-[14px] text-white/70 no-underline hover:text-white hover:bg-white/5 transition-colors"
        >
          <span className="text-[16px]">🏠</span>
          홈페이지로 가기
        </Link>
        <Link
          to="/admin/mypage"
          onClick={onClose}
          className="flex items-center gap-3 px-5 py-[10px] text-[14px] text-white/70 no-underline hover:text-white hover:bg-white/5 transition-colors"
        >
          <span className="text-[16px]">👤</span>
          마이페이지
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-5 py-[10px] text-[14px] text-red-400 bg-transparent border-none cursor-pointer hover:text-red-300 hover:bg-white/5 transition-colors"
        >
          <span className="text-[16px]">🚪</span>
          로그아웃
        </button>
      </div>
    </div>
  )

  return (
    <div style={{ width: '260px', height: '100vh', zIndex: 100 }} className="sticky top-0 shrink-0">
      {sidebarContent}
    </div>
  )
}